import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  InputAdornment,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import api from '../../services/api';

const PurchasesManagement = () => {
  const [purchases, setPurchases] = useState([]);
  const [computerParts, setComputerParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partsLoading, setPartsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    supplier_name: '',
    invoice_number: '',
    purchase_date: dayjs().format('YYYY-MM-DD'),
    items: [{ part_id: '', part_name: '', quantity: 1, unit_price: 0 }],
    notes: ''
  });

  useEffect(() => {
    fetchPurchases();
    fetchComputerParts();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Fetching purchases from API...');
      
      const response = await api.getPurchases();
      console.log('✅ API Response received:', response);
      console.log('📊 Response data:', response.data);
      
      if (response.data && response.data.success) {
        console.log(`✅ Found ${response.data.data?.length || 0} purchases`);
        setPurchases(response.data.data || []);
      } else {
        console.warn('⚠️ API response missing success flag');
        setPurchases([]);
      }
    } catch (error) {
      console.error('❌ Error fetching purchases:', error);
      setError(error.message);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchComputerParts = async () => {
    try {
      setPartsLoading(true);
      console.log('🔄 Fetching computer parts...');
      
      const response = await api.getComputerParts();
      console.log('✅ Computer parts response:', response.data);
      
      if (response.data && response.data.success) {
        console.log(`✅ Found ${response.data.data?.length || 0} computer parts`);
        setComputerParts(response.data.data || []);
      }
    } catch (error) {
      console.error('❌ Error fetching computer parts:', error);
      setComputerParts([]);
    } finally {
      setPartsLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      supplier_name: '',
      invoice_number: '',
      purchase_date: dayjs().format('YYYY-MM-DD'),
      items: [{ part_id: '', part_name: '', quantity: 1, unit_price: 0 }],
      notes: ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    
    if (field === 'part_id') {
      // Find the selected part
      const selectedPart = computerParts.find(part => part.id === parseInt(value));
      
      if (selectedPart) {
        newItems[index].part_id = value;
        newItems[index].part_name = selectedPart.part_name || selectedPart.name || '';
        // Auto-fill unit price if available
        if (selectedPart.purchase_price) {
          newItems[index].unit_price = parseFloat(selectedPart.purchase_price) || 0;
        }
      } else {
        newItems[index].part_id = value;
        newItems[index].part_name = '';
      }
    } else {
      newItems[index][field] = field === 'quantity' || field === 'unit_price' 
        ? parseFloat(value) || 0 
        : value;
    }
    
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { part_id: '', part_name: '', quantity: 1, unit_price: 0 }]
    }));
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: newItems
    }));
  };

  const handleSubmit = async () => {
    try {
      console.log('📤 Submitting purchase...');
      
      // Validate form
      if (!formData.supplier_name.trim()) {
        alert('Please enter supplier name');
        return;
      }

      if (formData.items.length === 0) {
        alert('Please add at least one item');
        return;
      }

      // Check if all items have valid part_id
      const invalidItems = formData.items.filter(item => !item.part_id || item.part_id === '');
      if (invalidItems.length > 0) {
        alert('Please select a valid part for all items');
        return;
      }

      // Prepare data
      const purchaseData = {
        supplier_name: formData.supplier_name,
        invoice_number: formData.invoice_number,
        purchase_date: formData.purchase_date,
        items: formData.items.map(item => ({
          part_id: parseInt(item.part_id),
          quantity: parseInt(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0
        })),
        notes: formData.notes
      };

      console.log('Purchase data to submit:', purchaseData);
      
      const response = await api.recordPurchase(purchaseData);
      console.log('Purchase response:', response.data);
      
      if (response.data && response.data.success) {
        alert('✅ Purchase recorded successfully!');
        handleCloseDialog();
        fetchPurchases();
      } else {
        alert('❌ Failed to record purchase: ' + (response.data?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error recording purchase:', error);
      alert('❌ Error: ' + (error.response?.data?.error || error.message));
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unit_price) || 0;
      return sum + (quantity * unitPrice);
    }, 0);
  };

  const addTestPurchase = async () => {
    try {
      // Get first available part or use default
      let partId = 1;
      if (computerParts.length > 0) {
        partId = computerParts[0].id;
      }
      
      const testData = {
        supplier_name: "Test Computer Store",
        invoice_number: `TEST-${Date.now()}`,
        purchase_date: dayjs().format('YYYY-MM-DD'),
        items: [
          {
            part_id: partId,
            quantity: 3,
            unit_price: 150.50
          }
        ],
        notes: "Test purchase for debugging"
      };
      
      console.log('Adding test purchase:', testData);
      const response = await api.recordPurchase(testData);
      console.log('Test purchase response:', response.data);
      
      if (response.data && response.data.success) {
        alert('✅ Test purchase added successfully!');
        fetchPurchases();
      } else {
        alert('❌ Failed to add test purchase');
      }
    } catch (error) {
      console.error('Error adding test purchase:', error);
      alert('❌ Error: ' + error.message);
    }
  };

  // Helper function to get purchase total
  const getPurchaseTotal = (purchase) => {
    return parseFloat(purchase.total_amount) || 0;
  };

  // Helper function to get item count
  const getItemCount = (purchase) => {
    return purchase.item_count || 0;
  };

  // Helper to get part name from part_id in purchase items
  const getPartName = (purchase) => {
    if (purchase.items && purchase.items.length > 0) {
      const firstItem = purchase.items[0];
      if (firstItem.part_name) {
        return firstItem.part_name;
      }
      // Try to find part name from computerParts list
      if (firstItem.part_id && computerParts.length > 0) {
        const part = computerParts.find(p => p.id === firstItem.part_id);
        return part ? part.part_name : `Part #${firstItem.part_id}`;
      }
      return `Part #${firstItem.part_id || 'N/A'}`;
    }
    return 'No items';
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading purchases...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 2 }}>
          Error loading purchases: {error}
        </Alert>
        <Button 
          startIcon={<RefreshIcon />} 
          onClick={fetchPurchases}
          sx={{ mt: 2 }}
          variant="contained"
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Purchases Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addTestPurchase}
            disabled={computerParts.length === 0}
            title={computerParts.length === 0 ? "Add computer parts first" : ""}
          >
            Add Test Purchase
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
            disabled={computerParts.length === 0}
            title={computerParts.length === 0 ? "Add computer parts first" : ""}
          >
            Record Purchase
          </Button>
        </Box>
      </Box>

      {computerParts.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No computer parts found. Please add computer parts in Inventory first.
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Invoice #</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Items</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Alert severity="info">
                      No purchases recorded. {computerParts.length === 0 
                        ? "Add computer parts first, then click 'Add Test Purchase'" 
                        : "Click 'Add Test Purchase' to add a sample purchase."}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                purchases.map((purchase) => {
                  const totalAmount = getPurchaseTotal(purchase);
                  const itemCount = getItemCount(purchase);
                  const partName = getPartName(purchase);
                  
                  return (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <ReceiptIcon />
                          {purchase.invoice_number || 'N/A'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {purchase.supplier_name}
                          </Typography>
                          {itemCount > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {partName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {dayjs(purchase.purchase_date).format('DD MMM YYYY')}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          Rs {totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${itemCount} items`} 
                          size="small" 
                          color={itemCount > 0 ? "primary" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" title="View Details">
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Record New Purchase
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supplier Name"
                  name="supplier_name"
                  value={formData.supplier_name}
                  onChange={handleInputChange}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Invoice Number"
                  name="invoice_number"
                  value={formData.invoice_number}
                  onChange={handleInputChange}
                  placeholder="Leave empty for auto-generated"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Purchase Date"
                  name="purchase_date"
                  type="date"
                  value={formData.purchase_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Purchase Items {partsLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
              </Typography>
              
              {computerParts.length === 0 ? (
                <Alert severity="warning">
                  No computer parts available. Please add computer parts in Inventory first.
                </Alert>
              ) : (
                <>
                  {formData.items.map((item, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Select Computer Part</InputLabel>
                            <Select
                              value={item.part_id}
                              label="Select Computer Part"
                              onChange={(e) => handleItemChange(index, 'part_id', e.target.value)}
                              required
                            >
                              <MenuItem value="">
                                <em>Select a part</em>
                              </MenuItem>
                              {computerParts.map((part) => (
                                <MenuItem key={part.id} value={part.id}>
                                  {part.part_name} 
                                  {part.part_number && ` (${part.part_number})`}
                                  {part.current_stock !== undefined && ` - Stock: ${part.current_stock}`}
                                  {part.purchase_price && ` - Price: Rs ${part.purchase_price}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            label="Quantity"
                            type="number"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            inputProps={{ min: 1 }}
                            required
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <TextField
                            fullWidth
                            label="Unit Price (Rs)"
                            type="number"
                            value={item.unit_price}
                            onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                            }}
                            inputProps={{ step: 0.01, min: 0 }}
                            required
                          />
                        </Grid>
                        <Grid item xs={6} md={2}>
                          <TextField
                            fullWidth
                            label="Total"
                            value={((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}
                            disabled
                            InputProps={{
                              startAdornment: <InputAdornment position="start">Rs</InputAdornment>,
                            }}
                          />
                        </Grid>
                        <Grid item xs={6} md={1}>
                          <IconButton 
                            onClick={() => removeItem(index)} 
                            color="error"
                            disabled={formData.items.length === 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                      {item.part_name && (
                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                          Selected: {item.part_name}
                        </Typography>
                      )}
                    </Paper>
                  ))}
                  <Button onClick={addItem} startIcon={<AddIcon />}>
                    Add Item
                  </Button>
                </>
              )}
            </Box>

            <TextField
              fullWidth
              label="Notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              multiline
              rows={2}
              placeholder="Additional notes..."
            />

            <Box sx={{ textAlign: 'right', pt: 2 }}>
              <Typography variant="h6">
                Total Amount: Rs {calculateTotal().toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!formData.supplier_name || formData.items.length === 0 || computerParts.length === 0}
          >
            Record Purchase
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PurchasesManagement;